import { defineMessages } from "react-intl";

import { Role } from "@mapmycustomers/shared";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";

const title = defineMessages({
  step1: {
    id: "guides.mapOnboarding.segmentPins.step1",
    defaultMessage: "Pin Customization",
  },
  step10: {
    id: "guides.mapOnboarding.segmentPins.step10",
    defaultMessage: "",
  },
  step2: {
    id: "guides.mapOnboarding.segmentPins.step2",
    defaultMessage: "There are three standard ways to segment your pins",
  },
  step3: {
    id: "guides.mapOnboarding.segmentPins.step3",
    defaultMessage: "",
  },
  step4: {
    id: "guides.mapOnboarding.segmentPins.step4",
    defaultMessage: "This is the Color Key",
  },
  step5: {
    id: "guides.mapOnboarding.segmentPins.step5",
    defaultMessage: "",
  },
  step6: {
    id: "guides.mapOnboarding.segmentPins.step6",
    defaultMessage: "",
  },
  step7: {
    id: "guides.mapOnboarding.segmentPins.step7",
    defaultMessage: "Here, you can create a custom legend by selecting a field",
  },
  step8: {
    id: "guides.mapOnboarding.segmentPins.step8",
    defaultMessage: "Click the dropdown to select a Company field",
  },
  step9: {
    id: "guides.mapOnboarding.segmentPins.step9",
    defaultMessage: "",
  },
});

const description = defineMessages({
  step1: {
    id: "guides.mapOnboarding.segmentPins.step1.description",
    defaultMessage:
      "Change pin colors and shapes based on criteria important to you!{br}" +
      "Click the Pin Icon to get started.",
  },
  step10: {
    id: "guides.mapOnboarding.segmentPins.step10.description",
    defaultMessage: '...and then click "Save"',
  },
  step2: {
    id: "guides.mapOnboarding.segmentPins.step2.description",
    defaultMessage:
      "🪣 Group Color {br}" +
      "📅 Date of Last Contact {br}" +
      "🚨 Frequency Status {br}" +
      "Pick one to move to the next step",
  },
  step3: {
    id: "guides.mapOnboarding.segmentPins.step3.description",
    defaultMessage: "Now, let's take a look at the color key.",
  },
  step4: {
    id: "guides.mapOnboarding.segmentPins.step4.description",
    defaultMessage:
      "Here, you can see a color legend. Last Contact colors are calculated in a linear scale. {br}" +
      "Other legends may show individual colors, such as viewing by Territory or Group.",
  },
  step5: {
    id: "guides.mapOnboarding.segmentPins.step5.description",
    defaultMessage: "Now, let's create a custom legend",
  },
  step6: {
    id: "guides.mapOnboarding.segmentPins.step6.description",
    defaultMessage: 'Click the dropdown, then click "+ Create Custom"',
  },
  step7: {
    id: "guides.mapOnboarding.segmentPins.step7.description",
    defaultMessage:
      "Each pin type (Companies, People, and Deals) can support different legends.{br}" +
      "<b>Note: only Single Option, Multi Option, and Numeric Fields support legends at this time.</b>",
  },
  step8: {
    id: "guides.mapOnboarding.segmentPins.step8.description",
    defaultMessage: "",
  },
  step9: {
    id: "guides.mapOnboarding.segmentPins.step9.description",
    defaultMessage: "Give the legend a name...",
  },
});

export const segmentPinsTour: TourDescriptor = {
  id: "map-onboarding-segment-pins",
  steps: [
    {
      id: "segment-pins-button",
      description: description.step1,
      options: {
        disableInteraction: false,
        showButtons: false,
      },
      selector:
        "div[class*=MapPage_container] div[class*=Toolbar_container] [data-menu-item=pinSettings]",
      title: title.step1,
      waitForEvent: {
        eventType: "click",
        selector:
          "div[class*=MapPage_container] div[class*=Toolbar_container] [data-menu-item=pinSettings]",
      },
    },
    {
      id: "select-pin-legend",
      description: description.step2,
      onAfter: async () => {
        const popover = document.querySelector(".ant-popover:not(.ant-popover-hidden)");
        if (!popover) {
          return;
        }

        const element = document.querySelector(
          "div[class*=MapPage_container] div[class*=Toolbar_container] div[class*=Toolbar_menuItem]:first-child"
        );
        (element as HTMLElement)?.click();
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector:
        ".ant-popover [class*=EntityPinSettings_selectField]:first-child .mmc-select-field__select",
      title: title.step2,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Map -> Pin Settings -> Color key",
      },
    },
    {
      id: "color-legend-button",
      description: description.step3,
      onAfter: async () => {
        const expandedSelector =
          "div[class*=MapPage_container] div[class*=Legends_expanded] div[class*=LegendCard_title]";

        const isExpanded = !!document.querySelector(expandedSelector);

        if (!isExpanded) {
          const element = document.querySelector(
            "div[class*=MapPage_container] div[class*=Legends_collapsed] > div:nth-child(1) > div"
          );
          if (element) {
            (element as HTMLElement).click();
          }
        }

        return new Promise((resolve) => waitForElement(expandedSelector, resolve));
      },
      selector:
        "div[class*=MapPage_container] div[class*=Legends_collapsed] > div:nth-child(1) > div > svg",
    },
    {
      id: "color-legend-owners",
      description: description.step4,
      eligibleRoles: [Role.OWNER],
      selector: "div[class*=MapPage_container] div[class*=Legends_expanded]",
      title: title.step4,
    },
    {
      id: "color-legend-other",
      description: description.step4,
      eligibleRoles: [Role.MANAGER, Role.MEMBER, Role.READ_ONLY, Role.READ_ONLY_WITH_EXPORTS],
      selector: "div[class*=MapPage_container] div[class*=Legends_expanded]",
      title: title.step4,
    },
    {
      id: "custom-legend-intro",
      description: description.step5,
      eligibleRoles: [Role.OWNER],
      onAfter: async () => {
        const element = document.querySelector(
          "div[class*=MapPage_container] div[class*=Toolbar_container] div[class*=Toolbar_menuItem]:first-child"
        );
        (element as HTMLElement)?.click();
        return new Promise((resolve) => {
          waitForElement(
            ".ant-popover [class*=EntityPinSettings_selectField]:first-child .mmc-select-field__select",
            resolve
          );
        });
      },
      selector:
        "div[class*=MapPage_container] div[class*=Toolbar_container] div[class*=Toolbar_menuItem]:first-child",
      title: title.step5,
    },
    {
      id: "custom-legend",
      description: description.step6,
      eligibleRoles: [Role.OWNER],
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector:
        ".ant-popover [class*=EntityPinSettings_selectField]:first-child .mmc-select-field__select",
      title: title.step6,
      waitForEvent: {
        eventType: "internal",
        selector: "Process: Map -> Create custom color legend for Companies",
      },
    },
    {
      id: "custom-legend-sidebar",
      description: description.step7,
      eligibleRoles: [Role.OWNER],
      selector: "[class*=Sidebar_container] .ant-drawer-content",
      title: title.step7,
    },
    {
      id: "custom-legend-sidebar-field",
      description: description.step8,
      eligibleRoles: [Role.OWNER],
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector: "[class*=FieldNameSelectField_container] .mmc-inline-label__content",
      title: title.step8,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Map -> Create custom color legend for Companies -> Details -> Field",
      },
    },
    {
      id: "custom-legend-sidebar-name",
      description: description.step9,
      eligibleRoles: [Role.OWNER],
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector:
        "[class*=Sidebar_container] .ant-drawer-content [class*=Sidebar_title] .mmc-editable-text",
      title: title.step9,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Map -> Create custom color legend for Companies -> Name",
      },
    },
    {
      id: "custom-legend-sidebar-save",
      description: description.step10,
      eligibleRoles: [Role.OWNER],
      onAfter: async () => {
        return new Promise((resolve) => {
          // wait for Preferences tab to become active (which happens after saving)
          waitForElement("[data-node-key=preferences].ant-tabs-tab-active", () => {
            const closeSidebarButton = document.querySelector(
              "[class*=Sidebar_container] .ant-drawer-close"
            );
            if (closeSidebarButton) {
              (closeSidebarButton as HTMLElement).click();
            }
            resolve();
          });
        });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector: "[class*=Sidebar_container] [class*=Footer] .mmc-button--primary",
      title: title.step10,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=Sidebar_container] [class*=Footer]",
      },
    },
  ],
};
